import { autoinject, computedFrom, observable } from "aurelia-framework";
import { Router, RouteConfig } from "aurelia-router";
import {
  IDisplaySettingsDto,
  ISchoolDto,
  ISchoolInternalDto,
  IStaffDto
} from "../../../../typings/project";
import { Marker } from "aurelia-google-maps";
import { HttpService } from "../../../services/http-service";
import { ApplicationSettings } from "../../../services/settings-service";
import { Paths } from "../../../paths";
import * as EmailValidator from "email-validator";

@autoinject()
export class SchoolDetails {
  itSchoolCode: string;
  school: ISchoolDto = <ISchoolDto | ISchoolInternalDto>{};
  displaySettings: IDisplaySettingsDto = <IDisplaySettingsDto>{};

  imageUrl: string;
  mapMarkers: Marker[] = [];
  internalUser: boolean;
  @observable search: string = null;
  initialStaffs: IStaffDto[];
  additionalRoleClasses: Record<string, string> = {
    "SAMS Admin": "sd-sams-admin",
    ICTC: "sd-ictc",
    "Finance Officer": "sd-finance-officer",
    Librarian: "sd-librarian"
  };

  staffTable: HTMLTableElement;
  staffSortButtons: HTMLDivElement;

  constructor(
    private readonly router: Router,
    private readonly httpService: HttpService,
    private readonly appSettings: ApplicationSettings
  ) { }

  activate = async (params: any, routeConfig: RouteConfig) => {
    this.itSchoolCode = params.itSchoolCode;
    await this.loadSchoolData();
    routeConfig.navModel.setTitle(this.school.name);
    this.initialStaffs = (this.school as ISchoolInternalDto).schoolStaff;
  };

  async attached() {
    this.scrollToTopOfSchoolDisplay();

    this.internalUser = this.appSettings.settings.isInternalUser;

    if (this.displaySettings.generalInformation.hasImage) {
    this.imageUrl =
      this.appSettings.apiUrl +
      Paths.getSchoolImagePath +
      encodeURI(this.itSchoolCode);
    }

    if (this.displaySettings.generalInformation.lat && this.displaySettings.generalInformation.long) {
      this.mapMarkers.push({
        latitude: this.school.lat,
        longitude: this.school.long,
        title: this.school.name,
      });
    }
  }

  async loadSchoolData() {
    this.displaySettings = await this.httpService.get<IDisplaySettingsDto>(
      Paths.getDisplaySettings,
    );

    if (this.appSettings.settings.isInternalUser) {
      this.school = await this.httpService.get<ISchoolInternalDto>(
        Paths.getSchoolPath,
        {
          itSchoolCode: this.itSchoolCode,
        },
      );
    } else {
      this.school = await this.httpService.get<ISchoolDto>(
        Paths.getSchoolPath,
        {
          itSchoolCode: this.itSchoolCode,
        },
      );
    }
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.mail",
    "displaySettings.generalInformation.councilMail",
  )
  get showMail(): boolean {
    return (
      !this.displaySettings ||
      (this.displaySettings.generalInformation.mail &&
        this.displaySettings.generalInformation.councilMail)
    );
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.hasImage",
    "school.hasImage",
  )
  get showImage(): boolean {
    return (
      (!this.displaySettings ||
        this.displaySettings.generalInformation.hasImage) &&
      this.school.hasImage
    );
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.AdditionalInformation",
    "school.AdditionalInformation.acaraId",
  )
  get showAcara(): boolean {
    return (
      !this.displaySettings ||
      this.displaySettings.generalInformation
        .generalInformationAdditionalInformation ||
      !!this.school.schoolAdditionalInformation.acaraId
    );
  }

  @computedFrom(
    "displaySettings.generalInformation.asnexId",
    "displaySettings.generalInformation.bamsCode",
  )
  get showAsnexOrBams(): boolean {
    return (
      this.displaySettings.generalInformation.asnexId ||
      this.displaySettings.generalInformation.bamsCode
    );
  }

  @computedFrom(
    "displaySettings.generalInformation.asnexId",
    "displaySettings.generalInformation.bamsCode",
    "school.asnexId",
  )
  get showBams(): boolean {
    if (!this.displaySettings.generalInformation.bamsCode) return false;

    return (
      !this.displaySettings.generalInformation.asnexId || !this.school.asnexId
    );
  }

  @computedFrom(
    "displaySettings.schoolManagement.firstName",
    "displaySettings.schoolManagement.lastName",
  )
  get showSchoolManagementName(): boolean {
    return (
      this.displaySettings.schoolManagement.firstName &&
      this.displaySettings.schoolManagement.lastName
    );
  }

  @computedFrom(
    "showSchoolManagementName",
    "displaySettings.schoolManagement.email",
  )
  get showSchoolManagementEmail(): boolean {
    return (
      this.showSchoolManagementName &&
      this.displaySettings.schoolManagement.email
    );
  }
  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.telephoneNumber",
    "displaySettings.generalInformation.facsimileTelephoneNumber",
  )
  get showPhoneFax(): boolean {
    return (!this.displaySettings || (this.displaySettings.generalInformation.telephoneNumber || this.displaySettings.generalInformation.facsimileTelephoneNumber));
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.lat",
    "displaySettings.generalInformation.long",
  )
  get showMap(): boolean {
    return this.displaySettings.generalInformation.lat && this.displaySettings.generalInformation.long;
  }

  @computedFrom(
    'canShowMap',
    "showImage"
  )
  get showImageMap(): boolean {
    return this.showMap || this.showImage;
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.mail",
    "displaySettings.generalInformation.councilMail",
  )
  get showEmail(): boolean {
    return (!this.displaySettings || (this.displaySettings.generalInformation.mail && this.displaySettings.generalInformation.councilMail));
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.religion",
    "displaySettings.generalInformation.directorate",
  )
  get showReligionDirectorate(): boolean {
    return (!this.displaySettings || (this.displaySettings.generalInformation.religion || this.displaySettings.generalInformation.directorate));
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.region",
    "displaySettings.generalInformation.generalInformationElectorate",
  )
  get showRegionElectorate(): boolean {
    return (!this.displaySettings || (this.displaySettings.generalInformation.region || this.displaySettings.generalInformation.generalInformationElectorate));
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.remoteDefinition",
    "displaySettings.generalInformation.ntgGeographicDefinition",
  )
  get showRemoteGeographic(): boolean {
    return (!this.displaySettings || (this.displaySettings.generalInformation.remoteDefinition || this.displaySettings.generalInformation.ntgGeographicDefinition));
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.preSchoolInfo",
    "school.preSchoolInfo",
  )
  get showPreSchool(): boolean {
    return (!this.displaySettings || this.displaySettings.generalInformation.preSchoolInfo) && !!this.school.preSchoolInfo;
  }

  @computedFrom(
    "displaySettings.preSchool",
    "displaySettings.preSchool.telephoneNumber",
    "displaySettings.preSchool.facsimileTelephoneNumber",
  )
  get showPreSchoolPhoneFax(): boolean {
    return (!this.displaySettings || (this.displaySettings.preSchool.telephoneNumber || this.displaySettings.preSchool.facsimileTelephoneNumber));
  }

  @computedFrom(
    "displaySettings.generalInformation",
    "displaySettings.generalInformation.schoolManagement",
    "school.schoolManagement",
  )
  get showSchoolManagement(): boolean {
    return (!this.displaySettings || this.displaySettings.generalInformation.schoolManagement) && (this.school.schoolManagement && this.school.schoolManagement.length > 0);
  }

  @computedFrom(
    "school.preSchool",
    "school.primarySchool",
    "school.middleSchool",
    "school.seniorSchool",
    "school.faftSchool"
  )
  get schoolType(): string {
    let types: string[] = [];
    if (this.school.faftSchool) types.push("FAFT School");
    if (this.school.preSchool) types.push("Preschool");
    if (this.school.primarySchool) types.push("Primary School");
    if (this.school.middleSchool) types.push("Middle School");
    if (this.school.seniorSchool) types.push("Senior School");
    return types ? types.join(", ") : "";
  }

  doSearch() {
    this.router.navigateToRoute("school-list");
  }

  isValidEmail = (email: string) => {
    return email && EmailValidator.validate(email);
  };

  scrollToTopOfSchoolDisplay() {
    $([document.documentElement, document.body]).animate(
      {
        scrollTop: $("main").offset().top
      },
      1000
    );
  }

  clearSearch() {
    //this will trigger SearchChanged
    this.search = null;
  }

  fullName(row: IStaffDto) {
    return `${row.lastName}, ${row.firstName}`;
  }


  sort(property: string, $event: Event) {
    const ths = this.staffTable.querySelectorAll('th');
    const descClass = 'aut-desc';
    const ascClass = 'aut-asc';

    const btn = $event.target as HTMLElement;
    this.resetBtnSortIndicator(btn);

    if (btn.classList.contains(ascClass)) {
      btn.classList.remove(ascClass);
      btn.classList.add(descClass);
    } else if (btn.classList.contains(descClass)) {
      btn.classList.remove(descClass);
    } else {
      btn.classList.add(ascClass);
    }

    for (let i = 0; i < ths.length; i++) {
      const th = ths.item(i);
      if (th.dataset.sort == 'fullName' && property == 'fullName') {
        th.click();
      } else if (th.dataset.sort == 'primaryRole' && property == 'primaryRole') {
        th.click();
      } else if (th.dataset.sort == 'phone' && property == 'phone') {
        th.click();
      }
    }
  }

  resetBtnSortIndicator(btnToIgnore: HTMLElement) {
    const btns = this.staffSortButtons.children;
    const descClass = 'aut-desc';
    const ascClass = 'aut-asc';

    for (let i = 0; i < btns.length; i++) {
      const btn = btns.item(i);

      if (btn == btnToIgnore) {
        continue;
      }
      btn.classList.remove(descClass);
      btn.classList.remove(ascClass);
    }
  }
}
